import { Link } from 'react-router-dom';
import { ChevronRight, Users, Coffee, Car, Briefcase, Clock, Shield } from 'lucide-react';

interface RelatedService {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
}

interface RelatedServicesProps {
  currentService: string;
  limit?: number;
}

function RelatedServices({ currentService, limit = 3 }: RelatedServicesProps) {
  // Define all services with their metadata
  const allServices: RelatedService[] = [
    {
      id: 'meet-greet',
      title: 'Meet and Greet Service',
      description: 'Personalized welcome and assistance throughout your airport journey',
      icon: <Users size={24} />,
      link: '/services#meet-greet',
    },
    {
      id: 'lounge',
      title: 'Lounge Services',
      description: 'Access to exclusive lounges with complimentary refreshments and amenities',
      icon: <Coffee size={24} />,
      link: '/services#lounge',
    },
    {
      id: 'chauffeur',
      title: 'Fleet Services & Chauffeur',
      description: 'Premium ground transportation with professional chauffeurs',
      icon: <Car size={24} />,
      link: '/services#chauffeur',
    },
    {
      id: 'baggage',
      title: 'Baggage Assistance',
      description: 'Professional handling and management of your luggage',
      icon: <Briefcase size={24} />,
      link: '/services#baggage',
    },
    {
      id: 'fast-track',
      title: 'Fast-Track Security',
      description: 'Expedited security and immigration clearance at major airports',
      icon: <Clock size={24} />,
      link: '/services#fast-track',
    },
    {
      id: 'concierge',
      title: 'Concierge Services',
      description: 'Bespoke assistance for special requests and unique requirements',
      icon: <Shield size={24} />,
      link: '/services#concierge',
    },
  ];

  // Filter out current service and get related ones
  const relatedServices = allServices
    .filter(service => service.id !== currentService.toLowerCase())
    .slice(0, limit);

  if (relatedServices.length === 0) {
    return null;
  }

  return (
    <section className="py-20 md:py-24 bg-gray-50 border-t border-gray-200">
      <div className="container mx-auto px-4">
        <div className="mb-12">
          <p className="eyebrow mb-3">Keep Exploring</p>
          <h2 className="section-title mb-4">Related Services</h2>
          <p className="text-gray-600">
            Explore our other premium services to enhance your travel experience
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {relatedServices.map((service) => (
            <Link
              key={service.id}
              to={service.link}
              className="card-surface p-8 group cursor-pointer"
            >
              <div className="bg-[#73BDF2]/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-4 group-hover:bg-[#73BDF2]/20 transition-colors duration-300">
                <div className="text-[#73BDF2]">{service.icon}</div>
              </div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-[#73BDF2] transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
              <div className="flex items-center text-[#73BDF2] font-semibold group-hover:translate-x-2 transition-transform duration-300">
                Learn More <ChevronRight size={18} className="ml-1" />
              </div>
            </Link>
          ))}
        </div>

        {/* Internal CTA Link */}
        <div className="mt-12 text-center">
          <Link
            to="/services"
            className="btn-primary"
          >
            View All Services <ChevronRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default RelatedServices;
